import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getDomain } from './constants';
import store from "../store"; // Import the Redux store
import { showError } from "../store/errorSlice"; // Import the action
import { getCookie } from './utils';
import {
  CHECKIN_TOKEN_HEADER,
  appointmentIdFromPath,
  checkinRefusalRedirect,
  clearCheckinToken,
  getCheckinToken,
  isCheckinApiUrl,
} from '../questionnaire/utils/checkinToken';
// Create axios instance with default config
const axiosInstance: AxiosInstance = axios.create({
  baseURL: getDomain(),
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getCookie('Authorized');
    const userId = localStorage.getItem('userid');
    
    // Only set Content-Type to application/json if it's not FormData
    // FormData needs to set its own Content-Type with boundary
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    } else {
      // Remove Content-Type header for FormData - browser will set it with boundary
      delete config.headers['Content-Type'];
    }
    

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    if (userId) {
      config.headers['X-User-Id'] = userId;
    }

    // STI-3024: check-in calls carry the access token for the page's appointment, never another
    // appointment's. Other calls never see it.
    if (isCheckinApiUrl(config.url)) {
      const checkinToken = getCheckinToken(appointmentIdFromPath(window.location.pathname));
      if (checkinToken) {
        config.headers[CHECKIN_TOKEN_HEADER] = checkinToken;
      }
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    // Callers that render their own inline errors (e.g. the signup/OTP flow) set skipGlobalError so a
    // failed request doesn't ALSO pop the app-wide error toast — which otherwise overlaps the local
    // message and, on the OTP screen, reads as "an error, then it redirected".
    const skipGlobalError = (error.config as (typeof error.config & { skipGlobalError?: boolean }))?.skipGlobalError;

    // STI-3024: a check-in call refused for its token (missing, expired, another appointment's, or a
    // cancelled/rescheduled appointment's) sends the patient to the date-of-birth page, instead of an
    // "Unauthorized" toast.
    const checkinRedirect = checkinRefusalRedirect(
      error.config?.url,
      error.response?.status,
      error.response?.data,
      window.location.pathname,
    );
    if (checkinRedirect) {
      clearCheckinToken(appointmentIdFromPath(window.location.pathname));
      window.location.assign(checkinRedirect);
      // Never settle: rejecting would run the caller's catch and flash its error ("Unable to load your
      // information") while the browser is already leaving for the verify page.
      return new Promise(() => undefined);
    }

    // Handle specific error cases
    if (error.response && !skipGlobalError) {
      switch (error.response.status) {
        case 401:
          const publicPaths = ['/', '/payments']; // Add your allowed paths here

          if(!publicPaths.includes(window.location.pathname)){
            store.dispatch(showError('Unauthorized'));
          }
          // window.location.href = '/error';
          break;
        case 500: {
          const beMessage = (error.response?.data as { message?: string } | undefined)?.message;
          store.dispatch(showError(beMessage || 'Something went wrong. Please try again later.'));
          break;
        }
      }
    }

    // Log all errors
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.message,
      data: error.response?.data
    });

    return Promise.reject(error);
  }
);

export default axiosInstance; 